import template from './menu.html';

import FilterButtons from './FilterButtons';
import ContentButtons from './ContentButtons';
import ToolButtons from '@/enum/ToolPresets';
import widgetSettingsIcon from '@/icons/widgetSettingsIcon.svg';
import logoAsset from '@/icons/logo-256x256.avif';

import renderButtons from './renderButtons';
import adjustFontSize from '@/tools/adjustFontSize';
import renderTools from './renderTools';
import reset from './reset';

import { ILanguage, LANGUAGES, resolveLanguageCode, getAvailableLanguages } from '@/i18n/Languages';

import css from './menu.css';
import enableContrast from '@/tools/enableContrast';
import { pluginConfig, pluginDefaults } from '@/config/pluginConfig';
import { userSettings, saveUserSettings } from '@/config/userSettings';
import { changeLanguage } from '@/i18n/changeLanguage';
import toggleMenu from './toggleMenu';
import { $widget, applyButtonPosition } from '../widget/widget';
import {
  DEFAULT_CUSTOM_PALETTE_STATE,
  ICustomPaletteState,
  CustomPaletteCategory,
} from '@/tools/customPalette';
import { t } from '@/i18n/translate';
import customPaletteIcon from '@/icons/customPaletteIcon.svg';
import { resolveWidgetSize, WidgetSizePreset } from '@/config/widgetSize';
import { ACCESSIBILITY_PROFILES } from '@/config/accessibilityProfiles';
import { ISettings, ISettingsStates } from '@/types/ISettings';
import { VERSION } from '@/version';

function hslToHex(h: number, s: number, l: number): string {
  const saturation = Math.max(0, Math.min(100, s)) / 100;
  const lightness = Math.max(0, Math.min(100, l)) / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const huePrime = (Math.max(0, Math.min(360, h)) % 360) / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));

  let r: number;
  let g: number;
  let b: number;

  if (huePrime >= 0 && huePrime < 1) {
    r = chroma;
    g = x;
    b = 0;
  } else if (huePrime >= 1 && huePrime < 2) {
    r = x;
    g = chroma;
    b = 0;
  } else if (huePrime >= 2 && huePrime < 3) {
    r = 0;
    g = chroma;
    b = x;
  } else if (huePrime >= 3 && huePrime < 4) {
    r = 0;
    g = x;
    b = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    r = x;
    g = 0;
    b = chroma;
  } else if (huePrime >= 5 && huePrime < 6) {
    r = chroma;
    g = 0;
    b = x;
  } else {
    r = 0;
    g = 0;
    b = 0;
  }

  const m = lightness - chroma / 2;
  const toHex = (value: number) => {
    const v = Math.round((value + m) * 255);
    return v.toString(16).padStart(2, '0');
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToHue(hex: string, fallback: number): number {
  const sanitized = hex.replace('#', '');
  if (![3, 6].includes(sanitized.length)) {
    return fallback;
  }

  const normalize =
    sanitized.length === 3
      ? sanitized
        .split('')
        .map((char) => char + char)
        .join('')
      : sanitized;

  const bigint = parseInt(normalize, 16);
  if (Number.isNaN(bigint)) {
    return fallback;
  }

  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  if (delta === 0) {
    return fallback;
  }

  let hue: number;

  if (max === rNorm) {
    hue = ((gNorm - bNorm) / delta) % 6;
  } else if (max === gNorm) {
    hue = (bNorm - rNorm) / delta + 2;
  } else {
    hue = (rNorm - gNorm) / delta + 4;
  }

  const degrees = Math.round((hue * 60 + 360) % 360);
  return degrees;
}

export function applyPanelWidth(width?: number) {
  const targetWidth =
    typeof width === 'number'
      ? width
      : pluginConfig.panelWidth ?? pluginDefaults.panelWidth ?? 500;
  const isSmall = pluginConfig.sizePreset === 'small' || targetWidth <= 400;
  const $menuRoot = document.querySelector<HTMLElement>('.visua11y-agent-menu');
  if ($menuRoot) {
    $menuRoot.style.setProperty('--visua11y-agent-panel-width', `${Math.round(targetWidth)}px`);
    $menuRoot.classList.toggle('visua11y-agent-menu-small', isSmall);
  }
  const sizePreset = pluginConfig.sizePreset;
  const sizeButtons = document.querySelectorAll<HTMLButtonElement>('.visua11y-agent-size-btn');
  sizeButtons.forEach((btn) => {
    const isSelected = Boolean(sizePreset) && btn.dataset.size === sizePreset;
    btn.classList.toggle('visua11y-agent-selected', isSelected);
    btn.setAttribute('aria-pressed', String(isSelected));
  });
}

export default function renderMenu() {
  const $container: HTMLElement = document.createElement('div');
  $container.innerHTML = `<style>${css}</style>` + template;

  const $menu = $container.querySelector<HTMLElement>('.visua11y-agent-menu');
  const setMenuAnchor = (position: string) => {
    if (!$menu) {
      return;
    }
    const isRight = position?.includes('right');
    if (isRight) {
      $menu.style.right = '0px';
      $menu.style.left = 'auto';
    } else {
      $menu.style.left = '0px';
      $menu.style.right = 'auto';
    }
  };

  if ($menu) {
    setMenuAnchor(pluginConfig?.position || 'bottom-left');
    const isSmall =
      pluginConfig.sizePreset === 'small' || (pluginConfig.panelWidth || 500) <= 400;
    $menu.classList.toggle('visua11y-agent-menu-small', isSmall);
  }

  const $logo = $container.querySelector<HTMLImageElement>('[data-visua11y-agent-logo]');
  if ($logo) {
    $logo.src = logoAsset;
  }

  const $version = $container.querySelector<HTMLElement>('[data-visua11y-agent-version]');
  if ($version) {
    $version.textContent = `v${VERSION}`;
  }

  ($menu.querySelector('.content') as HTMLElement).innerHTML = renderButtons(ContentButtons);
  ($menu.querySelector('.tools') as HTMLElement).innerHTML = renderButtons(
    ToolButtons,
    'visua11y-agent-tools'
  );

  const $contrastGrid = $menu.querySelector('.contrast') as HTMLElement;
  $contrastGrid.innerHTML = renderButtons(FilterButtons, 'visua11y-agent-filter');
  const $profilesGrid = $menu.querySelector<HTMLElement>('.visua11y-agent-profile-grid');

  const $profilesToggle = $menu.querySelector<HTMLButtonElement>('.visua11y-agent-profile-toggle');
  const $profilesCard = $menu.querySelector<HTMLElement>('.visua11y-agent-profiles-card');

  if ($profilesGrid) {
    $profilesGrid.innerHTML = ACCESSIBILITY_PROFILES.map((profile) => {
      const translatedLabel = t(profile.label);
      const translatedDescription = t(profile.description);

      const pillContent = profile.icon
        ? `<span class="visua11y-agent-profile-icon" aria-hidden="true">${profile.icon}</span>`
        : translatedLabel.slice(0, 2);

      return `
        <button
          type="button"
          class="visua11y-agent-profile-btn"
          data-profile="${profile.id}"
          aria-pressed="false"
        >
          <span class="visua11y-agent-profile-head">
            <span
              class="visua11y-agent-profile-label visua11y-agent-translate"
              data-translate="${profile.label}"
            >
              ${translatedLabel}
            </span>
            <span class="visua11y-agent-profile-pill" aria-hidden="true">
              ${pillContent}
            </span>
          </span>
          <span
            class="visua11y-agent-profile-desc visua11y-agent-translate"
            data-translate="${profile.description}"
          >
            ${translatedDescription}
          </span>
        </button>
      `;
    }).join('');
  }
  const profileButtons = Array.from(
    $menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-profile-btn')
  );

  const setProfilesVisibility = (expanded: boolean) => {
    if (!$profilesGrid || !$profilesCard || !$profilesToggle) {
      return;
    }
    $profilesToggle.setAttribute('aria-expanded', String(expanded));
    $profilesCard.classList.toggle('is-collapsed', !expanded);
    $profilesGrid.classList.toggle('is-collapsed', !expanded);
  };

  if ($profilesToggle) {
    setProfilesVisibility(false);
    $profilesToggle.addEventListener('click', () => {
      const expanded = $profilesToggle.getAttribute('aria-expanded') !== 'true';
      setProfilesVisibility(expanded);
    });
  }

  // *** States UI Rendering ***
  const filterButtons = Array.from(
    $menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-filter')
  );
  const $fontSizeAmount = $menu.querySelector<HTMLElement>('.visua11y-agent-amount');

  const contrastCycleOrder = ['contrast', 'dark-contrast', 'light-contrast', 'high-contrast'];
  const contrastLabelMap: Record<string, string> = {
    'dark-contrast': 'Dark Contrast',
    'light-contrast': 'Light Contrast',
    'high-contrast': 'High Contrast',
  };

  const $contrastCycleButton = $menu.querySelector<HTMLButtonElement>(
    '.visua11y-agent-filter[data-key="contrast-cycle"]'
  );
  const $contrastCycleLabel = $contrastCycleButton?.querySelector<HTMLSpanElement>(
    '.visua11y-agent-translate'
  );

  const colorBlindnessCycleOrder = [
    'color-blindness',
    'protanopia',
    'deuteranopia',
    'tritanopia',
    'achromatopsia',
  ];
  const colorBlindnessLabelMap: Record<string, string> = {
    protanopia: 'Protanopia',
    deuteranopia: 'Deuteranopia',
    tritanopia: 'Tritanopia',
    achromatopsia: 'Achromatopsia',
  };

  const $colorBlindnessCycleButton = $menu.querySelector<HTMLButtonElement>(
    '.visua11y-agent-filter[data-key="color-blindness-cycle"]'
  );
  const $colorBlindnessCycleLabel = $colorBlindnessCycleButton?.querySelector<HTMLSpanElement>(
    '.visua11y-agent-translate'
  );

  const updateContrastCycleButton = (value: string | false | 'contrast') => {
    if (!$contrastCycleButton || !$contrastCycleLabel) {
      return;
    }

    const isActive =
      typeof value === 'string' && value !== 'contrast' && contrastCycleOrder.includes(value);
    const translationKey = isActive
      ? contrastLabelMap[value as keyof typeof contrastLabelMap]
      : 'Contrast';

    $contrastCycleButton.classList.toggle('visua11y-agent-selected', isActive);
    $contrastCycleButton.setAttribute('aria-pressed', String(isActive));
    $contrastCycleLabel.setAttribute('data-translate', translationKey);
    $contrastCycleLabel.textContent = t(translationKey);

    const barsWrapper = $contrastCycleButton.querySelector<HTMLDivElement>(
      '.visua11y-agent-contrast-bars'
    );
    const barElements = Array.from(
      $contrastCycleButton.querySelectorAll<HTMLSpanElement>('.visua11y-agent-contrast-bar')
    );

    barElements.forEach((bar) => bar.classList.remove('is-active'));

    if (isActive) {
      barsWrapper?.classList.add('is-visible');
      const activeStepIndex = Math.max(0, contrastCycleOrder.indexOf(value as string) - 1);
      barElements.forEach((bar, index) => {
        bar.classList.toggle('is-active', index <= activeStepIndex);
      });
    } else {
      barsWrapper?.classList.remove('is-visible');
    }
  };

  const updateColorBlindnessCycleButton = (value?: unknown) => {
    if (!$colorBlindnessCycleButton || !$colorBlindnessCycleLabel) {
      return;
    }

    const isActive =
      typeof value === 'string' &&
      value !== 'color-blindness' &&
      colorBlindnessCycleOrder.includes(value);
    const translationKey = isActive
      ? colorBlindnessLabelMap[value as keyof typeof colorBlindnessLabelMap]
      : 'Color Blindness';

    $colorBlindnessCycleButton.classList.toggle('visua11y-agent-selected', isActive);
    $colorBlindnessCycleButton.setAttribute('aria-pressed', String(isActive));
    $colorBlindnessCycleLabel.setAttribute('data-translate', translationKey);
    $colorBlindnessCycleLabel.textContent = t(translationKey);
  };

  const getNextContrastValue = (current?: string | null) => {
    const index = current ? contrastCycleOrder.indexOf(current) : -1;
    const nextIndex = (index + 1) % contrastCycleOrder.length;
    return contrastCycleOrder[nextIndex];
  };

  const getNextColorBlindnessValue = (current?: string | null) => {
    const index = current ? colorBlindnessCycleOrder.indexOf(current) : -1;
    const nextIndex = (index + 1) % colorBlindnessCycleOrder.length;
    return colorBlindnessCycleOrder[nextIndex];
  };

  const syncStateSelections = () => {
    const states = userSettings?.states || {};
    const fontSizeValue = Number(states.fontSize) || 1;
    if ($fontSizeAmount) {
      $fontSizeAmount.textContent = `${Math.round(fontSizeValue * 100)}%`;
    }

    const buttons = Array.from($menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-btn'));
    buttons.forEach((btn) => {
      const key = btn.dataset.key;
      if (!key || btn.classList.contains('visua11y-agent-filter')) {
        return;
      }

      const isActive = Boolean(states[key]);
      btn.classList.toggle('visua11y-agent-selected', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    filterButtons.forEach((btn) => {
      const key = btn.dataset.key;
      if (!key || key === 'contrast-cycle' || key === 'color-blindness-cycle') {
        return;
      }
      const isActive = typeof states.contrast === 'string' && states.contrast === key;
      btn.classList.toggle('visua11y-agent-selected', isActive);
      btn.setAttribute('aria-pressed', String(isActive));
    });

    updateContrastCycleButton(states.contrast || 'contrast');
    updateColorBlindnessCycleButton(states['color-blindness-cycle'] || 'color-blindness');
  };

  syncStateSelections();

  // *** Widget Placement ***
  pluginConfig.position = userSettings.position || pluginConfig.position || 'bottom-left';
  const $positionToggle = $menu.querySelector<HTMLButtonElement>('.visua11y-agent-position-toggle');
  const $positionCard = $menu.querySelector<HTMLElement>('.visua11y-agent-position-card');
  const $settingsToggle = $menu.querySelector<HTMLButtonElement>('.visua11y-agent-settings-toggle');
  const $settingsCard = $menu.querySelector<HTMLElement>('.visua11y-agent-settings-card');
  const $settingsIcon = $menu.querySelector<HTMLElement>('.visua11y-agent-settings-icon');
  const $customPaletteCard = $menu.querySelector<HTMLElement>(
    '.visua11y-agent-custom-palette-card'
  );
  const $customPaletteIcon = $customPaletteCard?.querySelector<HTMLElement>(
    '.visua11y-agent-custom-palette-icon'
  );
  const $customPaletteTabs = Array.from(
    $menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-custom-palette-tab')
  );
  const $customPaletteRange = $menu.querySelector<HTMLInputElement>(
    '.visua11y-agent-custom-palette-range'
  );
  const $customPaletteReset = $menu.querySelector<HTMLButtonElement>(
    '.visua11y-agent-custom-palette-reset'
  );
  const $sizeButtons = Array.from(
    $menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-size-btn')
  );

  if ($settingsIcon) {
    $settingsIcon.innerHTML = widgetSettingsIcon;
  }

  const setSettingsVisibility = (expanded: boolean) => {
    if (!$settingsCard || !$settingsToggle) {
      return;
    }

    $settingsToggle.setAttribute('aria-expanded', String(expanded));
    $settingsCard.classList.toggle('visua11y-agent-settings-open', expanded);
  };

  if ($settingsToggle) {
    setSettingsVisibility(false);
    $settingsToggle.addEventListener('click', () => {
      const expanded = $settingsToggle.getAttribute('aria-expanded') !== 'true';
      setSettingsVisibility(expanded);
    });
  }

  const setPanelWidth = () => {
    applyPanelWidth();
  };

  let previousProfileSnapshot: {
    states: ISettingsStates;
    widgetSize?: ISettings['widgetSize'];
    sizeValue: number;
    panelWidth?: number;
    sizePreset?: WidgetSizePreset | null;
    position?: string;
    offset?: number[];
  } | null = null;

  const cloneStates = (states?: ISettingsStates): ISettingsStates =>
    JSON.parse(JSON.stringify(states || {}));

  const setSizePresetSelection = (preset: WidgetSizePreset | null) => {
    $sizeButtons.forEach((button) => {
      const isSelected = Boolean(preset) && button.dataset.size === preset;
      button.classList.toggle('visua11y-agent-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
  };

  const setActiveProfileButton = (profileId: string | null | undefined) => {
    profileButtons.forEach((button) => {
      const isSelected = Boolean(profileId) && button.dataset.profile === profileId;
      button.classList.toggle('visua11y-agent-selected', isSelected);
      button.setAttribute('aria-pressed', String(isSelected));
    });
  };

  const clearActiveProfileSelection = () => {
    if (userSettings.activeProfile) {
      userSettings.activeProfile = undefined;
    }
    setActiveProfileButton(null);
    previousProfileSnapshot = null;
  };

  const disableActiveProfile = () => {
    if (!userSettings.activeProfile) {
      return;
    }

    const snapshot = previousProfileSnapshot;
    userSettings.activeProfile = undefined;
    setActiveProfileButton(null);

    if (snapshot) {
      userSettings.states = cloneStates(snapshot.states);
      userSettings.widgetSize = snapshot.widgetSize;
      userSettings.position = snapshot.position;
      userSettings.offset = snapshot.offset ? [...snapshot.offset] : undefined;

      if (typeof snapshot.widgetSize !== 'undefined') {
        const resolved = resolveWidgetSize(snapshot.widgetSize);
        pluginConfig.size = resolved.size;
        pluginConfig.sizePreset = resolved.preset;
        pluginConfig.panelWidth = resolved.panelWidth;
        userSettings.widgetSize = snapshot.widgetSize;
        setSizePresetSelection(resolved.preset);
        setPanelWidth();
      } else {
        pluginConfig.size = snapshot.sizeValue;
        pluginConfig.sizePreset = snapshot.sizePreset || null;
        pluginConfig.panelWidth = snapshot.panelWidth ?? pluginConfig.panelWidth;
        userSettings.widgetSize = undefined;
        setSizePresetSelection(snapshot.sizePreset || null);
        setPanelWidth();
      }
      applyButtonPosition();

      if (snapshot.position) {
        pluginConfig.position = snapshot.position;
        setMenuAnchor(snapshot.position);
        syncPositionButtons();
      }

      if (snapshot.offset) {
        pluginConfig.offset = [...snapshot.offset];
      }
    } else {
      userSettings.states = {};
      userSettings.widgetSize = undefined;
      pluginConfig.sizePreset = null;
      pluginConfig.panelWidth = pluginDefaults.panelWidth;
      setSizePresetSelection(null);
      setPanelWidth();
    }

    previousProfileSnapshot = null;
    syncStateSelections();
    adjustFontSize(userSettings.states.fontSize || 1);
    renderTools();
    enableContrast(userSettings.states.contrast);
    saveUserSettings();
  };

  const applyProfilePreset = (profileId: string) => {
    const profile = ACCESSIBILITY_PROFILES.find((item) => item.id === profileId);
    if (!profile) {
      return;
    }

    previousProfileSnapshot = {
      states: cloneStates(userSettings.states),
      widgetSize: userSettings.widgetSize,
      sizeValue: pluginConfig.size,
      panelWidth: pluginConfig.panelWidth,
      sizePreset: pluginConfig.sizePreset,
      position: userSettings.position ?? pluginConfig.position,
      offset: Array.isArray(userSettings.offset)
        ? [...userSettings.offset]
        : Array.isArray(pluginConfig.offset)
          ? [...pluginConfig.offset]
          : undefined,
    };

    userSettings.activeProfile = profileId;
    userSettings.states = { ...(profile.states || {}) };

    if (profile.position) {
      pluginConfig.position = profile.position;
      userSettings.position = profile.position;
      setMenuAnchor(profile.position);
      syncPositionButtons();
    }

    if (Array.isArray(profile.offset)) {
      pluginConfig.offset = [...profile.offset];
      userSettings.offset = [...profile.offset];
    }

    if (typeof profile.widgetSize !== 'undefined') {
      const resolved = resolveWidgetSize(profile.widgetSize);
      pluginConfig.size = resolved.size;
      pluginConfig.sizePreset = resolved.preset;
      pluginConfig.panelWidth = resolved.panelWidth;
      userSettings.widgetSize = resolved.preset ?? resolved.size;
      setSizePresetSelection(resolved.preset);
      setPanelWidth();
    } else {
      setPanelWidth();
    }

    applyButtonPosition();
    syncStateSelections();
    setActiveProfileButton(profileId);

    adjustFontSize(userSettings.states.fontSize || 1);
    renderTools();
    enableContrast(userSettings.states.contrast);

    saveUserSettings();
  };

  profileButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const profileId = button.dataset.profile;
      if (!profileId) {
        return;
      }
      if (userSettings.activeProfile === profileId) {
        disableActiveProfile();
      } else {
        applyProfilePreset(profileId);
      }
    });
  });

  const initialSizeSource =
    typeof userSettings.widgetSize !== 'undefined'
      ? userSettings.widgetSize
      : pluginConfig.sizePreset || pluginConfig.size;
  const initialSize = resolveWidgetSize(initialSizeSource);
  setSizePresetSelection(initialSize.preset);
  pluginConfig.panelWidth = initialSize.panelWidth;
  setPanelWidth();
  setActiveProfileButton(userSettings.activeProfile);

  $sizeButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const presetValue = (button.dataset.size as WidgetSizePreset) || 'default';
      const resolved = resolveWidgetSize(presetValue);
      pluginConfig.size = resolved.size;
      pluginConfig.sizePreset = resolved.preset;
      pluginConfig.panelWidth = resolved.panelWidth;
      userSettings.widgetSize = resolved.preset ?? resolved.size;
      setSizePresetSelection(resolved.preset);
      setPanelWidth();
      clearActiveProfileSelection();
      saveUserSettings();
      applyButtonPosition();
    });
  });

  if ($customPaletteIcon) {
    $customPaletteIcon.innerHTML = customPaletteIcon;
  }

  const paletteDefaults = DEFAULT_CUSTOM_PALETTE_STATE.colors;
  const hueFallback: Record<CustomPaletteCategory, number> = {
    backgrounds: 0,
    headings: 210,
    contents: 220,
  };

  const categoryConfig: Record<CustomPaletteCategory, { saturation: number; lightness: number }> = {
    backgrounds: { saturation: 70, lightness: 92 },
    headings: { saturation: 85, lightness: 45 },
    contents: { saturation: 60, lightness: 28 },
  };

  const savedPalette =
    userSettings.states && typeof userSettings.states['custom-palette'] === 'object'
      ? (userSettings.states['custom-palette'] as ICustomPaletteState)
      : undefined;

  let paletteState: ICustomPaletteState = {
    enabled: DEFAULT_CUSTOM_PALETTE_STATE.enabled,
    activeCategory: DEFAULT_CUSTOM_PALETTE_STATE.activeCategory,
    colors: { ...paletteDefaults },
  };

  if (savedPalette) {
    paletteState = {
      enabled: Boolean(savedPalette.enabled),
      activeCategory: savedPalette.activeCategory ?? DEFAULT_CUSTOM_PALETTE_STATE.activeCategory,
      colors: { ...paletteDefaults, ...(savedPalette.colors ?? {}) },
    };

    if (paletteState.enabled) {
      userSettings.states['custom-palette'] = paletteState;
    }
  }

  const paletteHues: Record<CustomPaletteCategory, number> = {
    backgrounds: hexToHue(
      paletteState.colors?.backgrounds ?? paletteDefaults.backgrounds,
      hueFallback.backgrounds
    ),
    headings: hexToHue(
      paletteState.colors?.headings ?? paletteDefaults.headings,
      hueFallback.headings
    ),
    contents: hexToHue(
      paletteState.colors?.contents ?? paletteDefaults.contents,
      hueFallback.contents
    ),
  };

  const colorsMatchDefaults = () =>
    (['backgrounds', 'headings', 'contents'] as CustomPaletteCategory[]).every((category) => {
      const color = paletteState.colors?.[category] ?? paletteDefaults[category];
      return color.toLowerCase() === paletteDefaults[category].toLowerCase();
    });

  const setActiveTab = (category: CustomPaletteCategory) => {
    paletteState.activeCategory = category;
    $customPaletteTabs.forEach((tab) => {
      const isActive = tab.dataset.category === category;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
  };

  const createGradient = (category: CustomPaletteCategory) => {
    const { saturation, lightness } = categoryConfig[category];
    const stops = [0, 30, 60, 120, 180, 240, 300, 360];
    return `linear-gradient(90deg, ${stops
      .map(
        (h, index) => `${hslToHex(h, saturation, lightness)} ${(index / (stops.length - 1)) * 100}%`
      )
      .join(',')})`;
  };

  const updateSliderVisuals = (category: CustomPaletteCategory) => {
    if (!$customPaletteRange) {
      return;
    }

    const hue = paletteHues[category] ?? hueFallback[category];
    const gradient = createGradient(category);
    const currentColor = paletteState.colors?.[category] ?? paletteDefaults[category];

    $customPaletteRange.value = String(hue);
    $customPaletteRange.style.setProperty('--visua11y-agent-palette-gradient', gradient);
    $customPaletteRange.style.setProperty('--visua11y-agent-palette-thumb', currentColor);

    const barsWrapper = $customPaletteCard?.querySelector<HTMLDivElement>(
      '.visua11y-agent-custom-palette-bars'
    );
    if (barsWrapper) {
      const bars = Array.from(
        barsWrapper.querySelectorAll<HTMLSpanElement>('.visua11y-agent-custom-palette-bar')
      );
      bars.forEach((bar) => {
        const cat = (bar.dataset.category as CustomPaletteCategory) || 'backgrounds';
        const colorValue = paletteState.colors?.[cat] ?? paletteDefaults[cat];
        bar.style.background = colorValue;
        bar.classList.toggle('is-active', cat === category);
      });
    }
  };

  const persistPaletteState = (apply = true) => {
    clearActiveProfileSelection();
    const matchesDefaults = colorsMatchDefaults();
    paletteState.enabled = !matchesDefaults;

    if (paletteState.enabled) {
      userSettings.states['custom-palette'] = {
        enabled: true,
        activeCategory: paletteState.activeCategory,
        colors: { ...paletteDefaults, ...(paletteState.colors ?? {}) },
      };
    } else {
      delete userSettings.states['custom-palette'];
    }

    if (apply) {
      renderTools();
    }

    saveUserSettings();
  };

  const updatePaletteUI = () => {
    const category = paletteState.activeCategory ?? DEFAULT_CUSTOM_PALETTE_STATE.activeCategory;
    setActiveTab(category);
    updateSliderVisuals(category);
  };

  $customPaletteTabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const category =
        (tab.dataset.category as CustomPaletteCategory) ??
        DEFAULT_CUSTOM_PALETTE_STATE.activeCategory;
      setActiveTab(category);
      updateSliderVisuals(category);
    });
  });

  $customPaletteRange?.addEventListener('input', (event) => {
    const category = paletteState.activeCategory ?? DEFAULT_CUSTOM_PALETTE_STATE.activeCategory;
    const hue = Number((event.target as HTMLInputElement).value) || 0;
    paletteHues[category] = hue;

    const { saturation, lightness } = categoryConfig[category];
    const nextColor = hslToHex(hue, saturation, lightness);
    paletteState.colors = {
      ...paletteState.colors,
      [category]: nextColor,
    };

    paletteState.enabled = true;

    updateSliderVisuals(category);
    persistPaletteState(true);
  });

  $customPaletteReset?.addEventListener('click', () => {
    paletteState = {
      enabled: false,
      activeCategory: DEFAULT_CUSTOM_PALETTE_STATE.activeCategory,
      colors: { ...paletteDefaults },
    };

    (['backgrounds', 'headings', 'contents'] as CustomPaletteCategory[]).forEach((category) => {
      paletteHues[category] = hueFallback[category];
    });

    updatePaletteUI();
    persistPaletteState(true);
  });

  updatePaletteUI();

  const setPositionGridVisibility = (expanded: boolean) => {
    if (!$positionCard || !$positionToggle) {
      return;
    }

    $positionToggle.setAttribute('aria-expanded', String(expanded));
    $positionCard.classList.toggle('visua11y-agent-position-open', expanded);
  };

  if ($positionToggle) {
    setPositionGridVisibility(false);
    $positionToggle.addEventListener('click', () => {
      const expanded = $positionToggle.getAttribute('aria-expanded') !== 'true';
      if (expanded) {
        setSettingsVisibility(true);
      }
      setPositionGridVisibility(expanded);
    });
  }

  const positionButtons = Array.from(
    $menu.querySelectorAll<HTMLButtonElement>('.visua11y-agent-position-btn')
  );
  const syncPositionButtons = () => {
    positionButtons.forEach((button) =>
      button.classList.toggle('visua11y-agent-selected', button.dataset.position === pluginConfig.position)
    );
  };

  syncPositionButtons();

  positionButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const selectedPosition = button.dataset.position;
      if (!selectedPosition) {
        return;
      }

      positionButtons.forEach((btn) =>
        btn.classList.toggle('visua11y-agent-selected', btn === button)
      );

      pluginConfig.position = selectedPosition;
      userSettings.position = selectedPosition;
      clearActiveProfileSelection();
      setMenuAnchor(selectedPosition);
      saveUserSettings();
      applyButtonPosition();
      syncPositionButtons();
      setSettingsVisibility(true);
      setPositionGridVisibility(true);
    });
  });

  applyButtonPosition();

  // *** Translations ***
  userSettings.lang = resolveLanguageCode(userSettings.lang || pluginConfig?.lang);

  const $lang = $menu.querySelector<HTMLSelectElement>('#visua11y-agent-language');
  const $languageWrapper = $menu.querySelector<HTMLElement>('.visua11y-agent-language-wrapper');
  const $languageToggle = $menu.querySelector<HTMLButtonElement>('.visua11y-agent-menu-language');
  const $languagePanel = $menu.querySelector<HTMLElement>('#visua11y-agent-language-panel');
  const $languageSearch = $menu.querySelector<HTMLInputElement>('.visua11y-agent-language-search');
  const $languageList = $menu.querySelector<HTMLDivElement>('.visua11y-agent-language-list');
  const $languageEmpty = $menu.querySelector<HTMLElement>('.visua11y-agent-language-empty');

  let languagePanelOpen = false;

  const availableLanguages = getAvailableLanguages(pluginConfig.languages);
  if ($languageWrapper) {
    if (availableLanguages.length <= 1) {
      $languageWrapper.style.display = 'none';
      $languageWrapper.setAttribute('aria-hidden', 'true');
    } else {
      $languageWrapper.style.display = '';
      $languageWrapper.removeAttribute('aria-hidden');
    }
  }

  const escapeHTML = (value: string = ''): string =>
    value.replace(/[&<>"']/g, (match) => {
      const map: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',

        "'": '&#39;',
      };
      return map[match] || match;
    });

  const setLanguageSearchText = () => {
    if ($languageSearch) {
      $languageSearch.placeholder = t('Search languages');
    }
    if ($languageEmpty) {
      $languageEmpty.textContent = t('No languages found');
    }
  };

  const getActiveLanguageCode = () => resolveLanguageCode(userSettings.lang || pluginConfig?.lang);

  const updateLanguageToggleLabel = () => {
    if (!$languageToggle) {
      return;
    }
    const code = getActiveLanguageCode();
    const currentAvailable = getAvailableLanguages(pluginConfig.languages);
    const lang = currentAvailable.find((language) => language.code === code) || LANGUAGES.find((language) => language.code === code);
    const translatedLabel = t('Language');
    const label = `${translatedLabel}: ${lang?.label || code}`;
    $languageToggle.setAttribute('title', translatedLabel);
    $languageToggle.setAttribute('aria-label', label);
  };

  const renderLanguageList = (query: string = '') => {
    if (!$languageList) {
      return;
    }

    setLanguageSearchText();

    const normalized = query.trim().toLowerCase();
    const selectedCode = getActiveLanguageCode();
    const currentAvailable = getAvailableLanguages(pluginConfig.languages);

    const items = currentAvailable.filter((language) => {
      if (!normalized) {
        return true;
      }

      const label = language.label?.toLowerCase() || '';
      const code = language.code.toLowerCase();
      return label.includes(normalized) || code.includes(normalized);
    });

    if (!items.length) {
      $languageList.innerHTML = '';
      if ($languageEmpty) {
        $languageEmpty.hidden = false;
      }
      return;
    }

    if ($languageEmpty) {
      $languageEmpty.hidden = true;
    }

    const html = items
      .map((language) => {
        const isSelected = language.code === selectedCode;
        const safeCode = escapeHTML(language.code);
        const safeLabel = escapeHTML(language.label || language.code);
        const safeCodeLabel = escapeHTML(language.code.toUpperCase());

        return `<button type="button" class="visua11y-agent-language-option" data-lang="${safeCode}" data-selected="${String(isSelected)}" role="option" aria-selected="${String(isSelected)}">
                <span>${safeLabel}</span>
                <small>${safeCodeLabel}</small>
            </button>`;
      })
      .join('');

    $languageList.innerHTML = html;
  };

  const setLanguagePanelVisibility = (visible: boolean) => {
    languagePanelOpen = visible;
    if ($languageToggle) {
      $languageToggle.setAttribute('aria-expanded', String(visible));
    }
    if ($languagePanel) {
      $languagePanel.hidden = !visible;
    }

    if (visible) {
      renderLanguageList($languageSearch?.value || '');
      window.setTimeout(() => {
        $languageSearch?.focus();
      }, 0);
    } else if ($languageSearch) {
      $languageSearch.value = '';
      renderLanguageList('');
    }
  };

  const populateLanguageOptions = () => {
    if ($lang) {
      const langOptions = LANGUAGES.map(
        (lang: ILanguage) => `<option value="${lang.code}">${lang.label}</option>`
      ).join('');
      const previousValue = $lang.value;
      $lang.innerHTML = langOptions;

      const desiredValue = LANGUAGES.some((lang) => lang.code === userSettings.lang)
        ? userSettings.lang
        : resolveLanguageCode(previousValue);

      if (LANGUAGES.some((lang) => lang.code === desiredValue)) {
        $lang.value = desiredValue;
      }
    }

    renderLanguageList($languageSearch?.value || '');
    updateLanguageToggleLabel();
  };

  populateLanguageOptions();

  setLanguagePanelVisibility(false);

  $languageToggle?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    setLanguagePanelVisibility(!languagePanelOpen);
  });

  $languageSearch?.addEventListener('input', () => {
    renderLanguageList($languageSearch.value);
  });

  $languageList?.addEventListener('click', async (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>(
      '.visua11y-agent-language-option'
    );
    if (!target) {
      return;
    }

    const code = target.dataset.lang;
    if (!code) {
      return;
    }

    await changeLanguage(code);
    renderLanguageList($languageSearch?.value || '');
    updateLanguageToggleLabel();
    setLanguagePanelVisibility(false);
    $languageToggle?.focus();
  });

  const handleLanguagePanelOutsideClick = (event: MouseEvent) => {
    if (!languagePanelOpen) {
      return;
    }

    const target = event.target as Node | null;
    if ($languageWrapper && target && $languageWrapper.contains(target)) {
      return;
    }

    setLanguagePanelVisibility(false);
  };

  const handleLanguagePanelKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && languagePanelOpen) {
      setLanguagePanelVisibility(false);
      $languageToggle?.focus();
    }
  };

  document.addEventListener('mousedown', handleLanguagePanelOutsideClick);
  document.addEventListener('keydown', handleLanguagePanelKeydown);

  $lang?.addEventListener('change', async (event) => {
    await changeLanguage((event.target as HTMLSelectElement).value);
    renderLanguageList($languageSearch?.value || '');
    updateLanguageToggleLabel();
    setLanguagePanelVisibility(false);
  });

  const handleLanguagesUpdated = () => {
    populateLanguageOptions();
  };

  const handleLanguageChanged = () => {
    renderLanguageList($languageSearch?.value || '');
    updateLanguageToggleLabel();
  };

  document.addEventListener('visua11y-agent:languages:updated', handleLanguagesUpdated);
  document.addEventListener('visua11y-agent:language:changed', handleLanguageChanged);

  // *** Utils ***
  $container
    .querySelectorAll('.visua11y-agent-menu-close, .visua11y-agent-overlay')
    .forEach((el) => el.addEventListener('click', () => toggleMenu()));

  $container
    .querySelectorAll('.visua11y-agent-menu-reset')
    .forEach((el) => el.addEventListener('click', reset));

  // *** Controls ***
  $menu
    .querySelectorAll('.visua11y-agent-plus, .visua11y-agent-minus')
    .forEach((el: HTMLElement) => {
      el.addEventListener('click', () => {
        const difference = 0.1;

        let fontSize = userSettings?.states?.fontSize || 1;
        if (el.classList.contains('visua11y-agent-minus')) {
          fontSize -= difference;
        } else {
          fontSize += difference;
        }

        fontSize = Math.max(fontSize, 0.1);
        fontSize = Math.min(fontSize, 2);
        fontSize = Number(fontSize.toFixed(2));

        (document.querySelector('.visua11y-agent-amount') as HTMLElement).textContent =
          `${(fontSize * 100).toFixed(0)}%`;

        adjustFontSize(fontSize);
        userSettings.states.fontSize = fontSize;
        clearActiveProfileSelection();

        saveUserSettings();
      });
    });

  $menu.querySelectorAll('.visua11y-agent-btn').forEach((el: HTMLElement) => {
    el.addEventListener('click', () => {
      const key = el.dataset.key;
      const isSelected = !el.classList.contains('visua11y-agent-selected');
      clearActiveProfileSelection();

      if (el.classList.contains('visua11y-agent-filter')) {
        if (key === 'contrast-cycle') {
          filterButtons.forEach((button) => {
            if (button !== el && button.dataset.key !== 'color-blindness-cycle') {
              button.classList.remove('visua11y-agent-selected');
            }
          });

          const current =
            typeof userSettings.states.contrast === 'string'
              ? userSettings.states.contrast
              : 'contrast';
          const nextValue = getNextContrastValue(current);

          userSettings.states.contrast = nextValue === 'contrast' ? false : nextValue;
          updateContrastCycleButton(userSettings.states.contrast || 'contrast');
          enableContrast(userSettings.states.contrast);
          saveUserSettings();
          return;
        }

        if (key === 'color-blindness-cycle') {
          const current =
            typeof userSettings.states['color-blindness-cycle'] === 'string'
              ? userSettings.states['color-blindness-cycle']
              : 'color-blindness';
          const nextValue = getNextColorBlindnessValue(current);

          userSettings.states['color-blindness-cycle'] =
            nextValue === 'color-blindness' ? false : nextValue;
          updateColorBlindnessCycleButton(
            userSettings.states['color-blindness-cycle'] || 'color-blindness'
          );
          renderTools();
          saveUserSettings();
          return;
        }

        filterButtons.forEach((filterBtn) => filterBtn.classList.remove('visua11y-agent-selected'));

        if (isSelected) {
          el.classList.add('visua11y-agent-selected');
        }

        userSettings.states.contrast = isSelected ? key : false;
        updateContrastCycleButton(false);
        enableContrast(userSettings.states.contrast);
        saveUserSettings();
        return;
      }

      el.classList.toggle('visua11y-agent-selected', isSelected);
      userSettings.states[key] = isSelected;
      renderTools();
      saveUserSettings();
    });
  });

  // Apply saved footer settings
  const $footer = $container.querySelector<HTMLElement>('.visua11y-agent-footer');
  if ($footer) {
    if (userSettings.footerHidden) {
      $footer.classList.add('visua11y-agent-footer-hidden');
    }
    if (userSettings.footerSize) {
      if (userSettings.footerSize === 'small') {
        $footer.classList.add('visua11y-agent-footer-small');
      } else if (userSettings.footerSize === 'large') {
        $footer.classList.add('visua11y-agent-footer-large');
      }
    }
  }

  $widget.appendChild($container);

  return $container;
}
